/**
 * Init applier — wraps initGame for action-dispatcher symmetry.
 *
 * The Init applier ignores the incoming `state` (there is none at game start).
 * It adapts the InitAction shape to InitGameInput and delegates to initGame().
 *
 * Spec §6.1, §12.
 */

import { initGame } from "../init-game";
import type { InitAction } from "../types/actions";
import type { GameState, ScoringCardSelection } from "../types/game-state";

export const applyInit = (
  _state: GameState | null,
  action: InitAction,
): GameState => {
  // Build ScoringCardSelection from the wildlife/cardId pairs in the action.
  // The action has at most 5 pairs (one per species); we assign each to the
  // correct key rather than using reduce+spread which triggers the
  // no-accumulating-spread lint rule.
  const cards: Partial<ScoringCardSelection> = {};
  for (const { wildlife, cardId } of action.scoringCards) {
    (cards as Record<string, string>)[wildlife] = cardId;
  }
  const scoringCards = cards as ScoringCardSelection;

  const result = initGame({
    gameId: `game-${action.seed}`,
    seed: action.seed,
    playerSeating: action.playerSeating.map((p) => ({
      playerId: p.playerId,
      displayName: p.displayName,
    })),
    scoringCards,
    modulesOn: action.modulesOn,
    hostPlayerId: action.hostPlayerId,
    createdAt: new Date().toISOString(),
  });

  if (!result.ok) {
    throw new Error(`applyInit failed: ${result.error}`);
  }

  return result.value;
};
